import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FocusTrap from 'focus-trap-react';
import CancelIcon from '../assets/icons/CancelIcon';
import {
  DigitalLeadsWrapper,
  DigitalLeadsModal,
  CloseModal,
  Content,
  Header,
  InputSection,
  VehicleDetailSection,
  Title,
  Inputs,
  InputField,
  BlackBackground,
  Sticky,
  SectionWrapper,
} from './DitigalLeads.styles';
import VehicleInfo from './VehicleInfo/VehicleInfo';
import Submit from './Submit';
import useDealerInfo from '../hooks/useDealerInfo';
import useVehicleInfo from '../hooks/useVehicleInfo';
import useLeadsBuilder from '../hooks/useLeadsBuilder';
import useLockBodyScroll from '../hooks/useLockBodyScroll';
import LegalDisclaimer from './LegalDisclaimer';
import ResultModal from './ResultModal';
import { selectLocalConfig, setModalIsClose } from '../redux/localConfigReducer';
import { selectForm } from '../redux/formReducer';
import { removeVehicleInfo, selectDealerAndVehicleData } from '../redux/dealerAndVehicleReducer';
import useSourceOfLead from '../hooks/useSourceOfLead';
import {
  useGenericFormLoad,
  useSuccessfulFormSubmission,
  useUnsuccessfulFormError,
} from '../hooks/useIxisEvents';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import { FEATURE_APP_DETAILS } from '../constants';
import { selectAppConfig, setRemoveOffers } from '../redux/appConfigReducer';
import useIxisFormTracking from '../hooks/useIxisFormTracking';

const DigitalLeads: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const digitalLeadsRef = useRef(null);

  const { isModalOpen, leadId, error, errorMessage } = useSelector(selectLocalConfig);
  const contactForm = useSelector(selectForm);
  const { offers, dealerId, market } = useSelector(selectAppConfig);
  const { vehicle, vehicleByCarline, vehicleBuAudiCode } = useSelector(selectDealerAndVehicleData);
  const vehicleInfo = vehicle || vehicleByCarline || vehicleBuAudiCode;

  const promotion = React.useMemo(
    () => (offers ? convertToPromotionsType(offers) : undefined),
    [offers],
  );

  const { dealer, isLoadingDealer } = useDealerInfo();
  const { isVehicleLoading } = useVehicleInfo();
  const { topicOfInterestInput, tradeInVehicleInputs, modelSelections, buildedForm } =
    useLeadsBuilder();
  const { specificSource: leadSource } = useSourceOfLead();

  const [prevVehicle, setPrevVehicle] = useState(vehicleInfo);
  const vehicleInView = vehicleInfo ?? prevVehicle;
  const isAudiMake = vehicle?.titleText?.includes('Audi') ?? false;

  const excludeYearFromModelName = useCallback(
    (currentVehicleIsAudi?: boolean) => {
      if (!vehicleInView) return undefined;
      return currentVehicleIsAudi
        ? vehicleInView.modelName?.toString().slice(5)
        : vehicleInView.modelName;
    },
    [vehicleInView],
  );

  const {
    vehicleStatus,
    vehicleSaleType,
    vehicleSaleOrderType,
    orderStatusVehicle,
    vehicleWithEditedNames,
    vehicleDataForPromotions,
  } = useIxisFormTracking({
    isModalOpen,
    vehicle,
    vehicleInView,
    offers,
    dealerId,
    leadSource,
    contactForm,
    promotion,
    isAudiMake,
    excludeYearFromModelName,
  });

  const handleCloseModal = () => {
    dispatch(setRemoveOffers());
    dispatch(removeVehicleInfo());
    dispatch(setModalIsClose());
    window.history.pushState('', document.title, window.location.pathname + window.location.search);
  };

  useLockBodyScroll(isModalOpen);

  // Generic IXIS hooks remain as before
  useGenericFormLoad({ isModalOpen, leadSource, vehicle: undefined });
  useSuccessfulFormSubmission({
    contactForm,
    leadId,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });
  useUnsuccessfulFormError({
    error,
    errorMessage,
    contactForm,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  const isGeneralForm = !!topicOfInterestInput;
  let formName: string;

  if (isGeneralForm) {
    formName = 'Generic Lead';
  } else if (leadSource === 'special_offers' || leadSource === 'offers') {
    formName = 'Promotions Lead';
  } else {
    formName = 'VTP Lead';
  }

  return (
    <>
      {isModalOpen && (
        <DigitalLeadsWrapper>
          <FocusTrap
            active={isModalOpen}
            focusTrapOptions={{ escapeDeactivates: false, clickOutsideDeactivates: true }}
          >
            {!leadId && !error ? (
              <DigitalLeadsModal
                ref={digitalLeadsRef}
                data-digital-leads-form-form-name={formName}
                data-digital-leads-form-form-type="lead"
                data-digital-leads-form-form-data-model-of-interest-model-family={
                  vehicleInView?.modelName || 'null'
                }
                data-digital-leads-form-form-data-preferred-dealer={
                  vehicleInView?.dealerId || 'null'
                }
                data-digital-leads-vehicle-audi-code={vehicleInView?.audiCode || 'null'}
                data-digital-leads-vehicle-dealer-code={vehicleInView?.dealerId || 'null'}
                data-digital-leads-vehicle-dealer-name={vehicleInView?.dealerName || 'null'}
                data-digital-leads-vehicle-fuel-type={vehicleInView?.fuelType || 'null'}
                data-digital-leads-vehicle-inventory-type={
                  vehicleInView?.vehicleInventoryType || 'null'
                }
                data-digital-leads-vehicle-model-code={vehicleInView?.modelCode || 'null'}
                data-digital-leads-vehicle-model-name={
                  excludeYearFromModelName(isAudiMake) || 'null'
                }
                data-digital-leads-vehicle-model-year={vehicleInView?.modelYear || 'null'}
                data-digital-leads-vehicle-price={vehicleInView?.modelPrice || 'null'}
                data-digital-leads-vehicle-price-dealer={vehicleInView?.modelPriceRetail || 'null'}
                data-digital-leads-vehicle-sale-type={vehicleSaleType || 'null'}
                data-digital-leads-vehicle-sale-order-type={vehicleSaleOrderType || 'null'}
                data-digital-leads-vehicle-order-status={orderStatusVehicle || 'null'}
                data-digital-leads-vehicle-status={vehicleStatus || 'null'}
                data-digital-leads-vehicle-stock-number={vehicleInView?.stockNumber || 'null'}
                data-digital-leads-vehicle-vin={vehicleInView?.vin || 'null'}
                data-digital-leads-feature-app-base-url={FEATURE_APP_DETAILS.baseUrl}
                data-digital-leads-feature-app-id={FEATURE_APP_DETAILS.id}
                data-digital-leads-feature-app-name={FEATURE_APP_DETAILS.name}
              >
                <Sticky>
                  <CloseModal onClick={handleCloseModal} aria-label="close">
                    <CancelIcon />
                  </CloseModal>
                </Sticky>
                <Header>
                  {dealer?.name
                    ? t('digitalLeads.contactDealer', { dealerName: dealer.name })
                    : t('digitalLeads.contactDealerInYourArea')}
                </Header>
                <Content>
                  <InputSection>
                    <Inputs>
                      {topicOfInterestInput?.map((topic) => (
                        <InputField key={topic.inputKey}>{topic.Component}</InputField>
                      ))}
                      {tradeInVehicleInputs && (
                        <SectionWrapper>
                          <Title>{t('digitalLeads.inputs.vehicleDetails')}</Title>
                          {tradeInVehicleInputs.map((input) => (
                            <InputField key={input.inputKey}>{input.Component}</InputField>
                          ))}
                        </SectionWrapper>
                      )}
                      {modelSelections && (
                        <SectionWrapper>
                          <Title>{t('digitalLeads.inputs.modelSelection')}</Title>
                          {modelSelections.map((model) => (
                            <InputField key={model.inputKey}>{model.Component}</InputField>
                          ))}
                        </SectionWrapper>
                      )}
                      <Title>{t('digitalLeads.contactInfoLabel')}</Title>
                      {buildedForm.map((input) => (
                        <InputField key={input.inputKey}>{input.Component}</InputField>
                      ))}
                    </Inputs>
                    <LegalDisclaimer />
                    <Submit />
                  </InputSection>
                  <VehicleDetailSection>
                    <VehicleInfo
                      dealer={dealer}
                      vehicle={vehicleInView}
                      isGeneralForm={isGeneralForm}
                      isLoadingDealer={isLoadingDealer}
                      isVehicleLoading={isVehicleLoading}
                    />
                  </VehicleDetailSection>
                </Content>
              </DigitalLeadsModal>
            ) : (
              <ResultModal handleCloseModal={handleCloseModal} isSuccess={!!leadId} />
            )}
          </FocusTrap>
          <BlackBackground onClick={handleCloseModal} />
        </DigitalLeadsWrapper>
      )}
    </>
  );
};

export default DigitalLeads;
